import React, { useState, useEffect } from 'react';
import { useClients, PIPELINE_STAGES } from '../context/ClientsContext';
import { useUsers } from '../context/UsersContext';
import { useConfig } from '../context/ConfigContext';
import { C, S } from '../utils/styles';
import Icon from './Icon';
import { fmt, formatPhone } from '../utils/formatters';
import AnalysisConfigEditor from './AnalysisConfigEditor';
import EnablePortalAccessModal from './EnablePortalAccessModal';
import api from '../utils/api';
import StyledSelect from './StyledSelect';
import { parseCSV } from '../utils/calculations';
import { buildClientSummaryFromEmployees } from '../utils/buildClientSummary';
import MessagesSection from './portal/MessagesSection';
import Section105Wizard from './ibos/Section105Wizard';
import SavedOptionsAdminPanel from './admin/SavedOptionsAdminPanel';
import SmartCensusUpload from './employer/SmartCensusUpload';

export default function ClientDetailDrawer({ clientId, onClose, onOpenAnalysis }) {
  const { clients, updateCompanyInfo, updateCRM, saveContact, deleteContact, addNote, saveClient, deleteClient, updateAnalysisConfig, lockAnalysisConfig, unlockAnalysisConfig, enablePortalAccess, disablePortalAccess, resetContactPassword } = useClients();
  const { currentUser, getUserById } = useUsers();
  const config = useConfig();
  const [editingCompany, setEditingCompany] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingAnalysisConfig, setEditingAnalysisConfig] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [newNote, setNewNote] = useState('');
  const [showEnablePortalModal, setShowEnablePortalModal] = useState(null); // Store contact ID when showing modal
  const [portalError, setPortalError] = useState(null);
  const [showActivityLog, setShowActivityLog] = useState(false); // Collapsed by default
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [assigningContracts, setAssigningContracts] = useState(false);
  const [showSmartUpload, setShowSmartUpload] = useState(false);
  
  // Load contract templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await api.config.getContractTemplates();
        setContractTemplates(templates);
      } catch (error) {
        console.error('Failed to load contract templates:', error);
      }
    };
    loadTemplates();
  }, []);
  
  // Lock body scroll while drawer is open to prevent layout shift
  // when scrollbar appears/disappears under the overlay.
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    // Compensate for scrollbar disappearance to avoid shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, []);
  
  const client = clients.find(c => c.id === clientId);
  
  if (!client) return null;
  
  const handleAssignContracts = async (assignmentType = 'manual') => {
    if (selectedTemplates.length === 0 && assignmentType === 'manual') return;
    
    setAssigningContracts(true);
    try {
      const templatesToAssign = assignmentType === 'auto' 
        ? contractTemplates.filter(t => t.isRequired).map(t => t.id)
        : selectedTemplates;
      
      console.log('Assigning contracts:', { clientId: client.id, templatesToAssign, assignmentType });
      
      await api.clients.assignContracts(client.id, templatesToAssign, assignmentType);
      
      console.log('Contracts assigned successfully');
      
      // Refresh client data by fetching updated client
      const updatedClient = await api.clients.get(client.id);
      
      // Update the client using saveClient from context
      saveClient(updatedClient);
      
      // Clear selected templates
      setSelectedTemplates([]);
    } catch (error) {
      console.error('Failed to assign contracts - Full error:', error);
      alert(`Failed to assign contracts: ${error.message || 'Unknown error'}`);
    } finally {
      setAssigningContracts(false);
    }
  };
  
  const handleToggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };
  
  const handleSaveCompany = () => {
    saveClient(client);
    setEditingCompany(false);
  };
  
  const handleDeleteClient = () => {
    const companyName = client.company?.legalName || client.company?.dba || 'this client';
    const contactCount = client.contacts?.length || 0;
    const proposalCount = client.proposals?.length || 0;
    
    const confirmMessage = `⚠️ PERMANENT DELETE WARNING ⚠️\n\n` +
      `You are about to delete:\n` +
      `• Client: ${companyName}\n` +
      `• ${contactCount} contact(s) and their user accounts\n` +
      `• ${proposalCount} proposal(s)\n` +
      `• All contracts, notes, and activity history\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type the company name to confirm: "${companyName}"`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput === companyName) {
      deleteClient(clientId);
      onClose(); // Close drawer after deletion
    } else if (userInput !== null) {
      alert('Company name did not match. Deletion cancelled.');
    }
  };
  
  // Compute button states based on available data
  const hasAnalysisData = client.employees && client.employees.length > 0;
  const hasSummary = client.summary && client.summary.totalEmployees > 0;
  
  const handleOpenAnalysis = () => {
    if (!hasAnalysisData) return;
    console.log('Opening analysis for client:', clientId);
    if (onOpenAnalysis) {
      console.log('Calling onOpenAnalysis callback');
      onOpenAnalysis(clientId); // Pass client ID and navigate
    } else {
      console.warn('onOpenAnalysis callback not provided');
    }
    onClose(); // Close the drawer AFTER triggering navigation
  };
  
  const handleGeneratePDF = () => {
    if (!hasSummary) return;
    // Logic to generate PDF
    // This would typically call a PDF generation function
    console.log('Generate PDF for client:', client.company.legalName);
  };
  
  const handleAddNote = () => {
    if (newNote.trim()) {
      addNote(newNote.trim());
      setNewNote('');
    }
  };
  
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 600,
          background: C.card,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.2)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: '20px 24px', 
          borderBottom: `1px solid ${C.border}`,
          position: 'sticky',
          top: 0,
          background: C.card,
          zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>
                {client.company.legalName || 'Unnamed Client'}
              </h3>
              
              <StyledSelect
                value={client.crm.stage}
                onValueChange={(val) => updateCRM({ stage: val })}
                options={Object.entries(PIPELINE_STAGES).map(([key, stage]) => ({
                  value: key,
                  label: stage.label
                }))}
                style={{ width: 'auto', fontSize: 12, fontWeight: 600 }}
              />
            </div>
            
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              style={{
                padding: 8,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: C.textMid,
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                touchAction: 'manipulation',
              }}
            >
              <Icon name="close" style={{ fontSize: 24 }} />
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenAnalysis();
              }}
              disabled={!hasAnalysisData}
              style={{
                ...S.button,
                padding: '6px 12px',
                fontSize: 12,
                background: hasAnalysisData ? C.blueLight : C.bg,
                color: hasAnalysisData ? C.blue : C.textLight,
                border: `1px solid ${hasAnalysisData ? C.blue : C.border}`,
                cursor: hasAnalysisData ? 'pointer' : 'not-allowed',
                opacity: hasAnalysisData ? 1 : 0.5,
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                touchAction: 'manipulation',
              }}
              title={!hasAnalysisData ? 'Upload census data first' : 'Open in Workforce tab'}
            >
              <Icon name="bar-chart-outline" style={{ fontSize: 14 }} />
              Open Analysis
            </button>
            
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleGeneratePDF();
              }}
              disabled={!hasSummary}
              style={{
                ...S.button,
                padding: '6px 12px',
                fontSize: 12,
                background: hasSummary ? C.greenLight : C.bg,
                color: hasSummary ? C.green : C.textLight,
                border: `1px solid ${hasSummary ? C.green : C.border}`,
                cursor: hasSummary ? 'pointer' : 'not-allowed',
                opacity: hasSummary ? 1 : 0.5,
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                touchAction: 'manipulation',
              }}
              title={!hasSummary ? 'Run analysis first' : 'Generate PDF report'}
            >
              <Icon name="document-outline" style={{ fontSize: 14 }} />
              Generate PDF
            </button>
            
            {/* Delete button - Super User only */}
            {currentUser?.role === 'super_user' && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClient();
                }}
                style={{
                  ...S.button,
                  padding: '6px 12px',
                  fontSize: 12,
                  background: C.redLight,
                  color: C.red,
                  border: `1px solid ${C.red}`,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                  touchAction: 'manipulation',
                }}
                title="Permanently delete client and all associated data (Super User only)"
              >
                <Icon name="trash-outline" style={{ fontSize: 14 }} />
                Delete
              </button>
            )}
          </div>
        </div>
        
        {/* Content */}
        <div style={{ padding: 24 }}>
          {/* Company Information Section */}
          <section style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                COMPANY INFORMATION
              </h4>
              {!editingCompany ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingCompany(true);
                  }}
                  style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.blue,
                    background: 'transparent',
                    border: `1px solid ${C.blue}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                    touchAction: 'manipulation',
                  }}
                >
                  <Icon name="pencil-outline" style={{ fontSize: 12 }} /> Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditingCompany(false);
                    }}
                    style={{
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.textMid,
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                      touchAction: 'manipulation',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSaveCompany();
                    }}
                    style={{
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#fff',
                      background: C.green,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                      touchAction: 'manipulation',
                    }}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.textMid, display: 'block', marginBottom: 4 }}>
                  Legal Name *
                </label>
                {editingCompany ? (
                  <input
                    type="text"
                    value={client.company.legalName}
                    onChange={(e) => updateCompanyInfo({ legalName: e.target.value })}
                    placeholder="Acme Corporation LLC"
                    style={S.input}
                  />
                ) : (
                  <div style={{ fontSize: 13, color: C.text }}>
                    {client.company.legalName || <span style={{ color: C.textLight }}>Not set</span>}
                  </div>
                )}
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.textMid, display: 'block', marginBottom: 4 }}>
                    DBA / Trade Name
                  </label>
                  {editingCompany ? (
                    <input
                      type="text"
                      value={client.company.dba}
                      onChange={(e) => updateCompanyInfo({ dba: e.target.value })}
                      placeholder="Acme Store #123"
                      style={S.input}
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: C.text }}>
                      {client.company.dba || <span style={{ color: C.textLight }}>Not set</span>}
                    </div>
                  )}
                </div>
                
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.textMid, display: 'block', marginBottom: 4 }}>
                    Industry
                  </label>
                  {editingCompany ? (
                    <StyledSelect
                      value={client.company.industry || undefined}
                      onValueChange={(val) => updateCompanyInfo({ industry: val })}
                      options={[
                        { value: "Quick Service Restaurant", label: "Quick Service Restaurant" },
                        { value: "Hospitality/Hotel", label: "Hospitality/Hotel" },
                        { value: "Retail", label: "Retail" },
                        { value: "Healthcare", label: "Healthcare" },
                        { value: "Home Services", label: "Home Services" },
                        { value: "Logistics/Delivery", label: "Logistics/Delivery" },
                        { value: "Manufacturing", label: "Manufacturing" },
                        { value: "Professional Services", label: "Professional Services" },
                        { value: "Other", label: "Other" }
                      ]}
                      placeholder="Select..."
                    />
                  ) : (
                    <div style={{ fontSize: 13, color: C.text }}>
                      {client.company.industry || <span style={{ color: C.textLight }}>Not set</span>}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.textMid, display: 'block', marginBottom: 4 }}>
                  Address
                </label>
                {editingCompany ? (
                  <>
                    <input
                      type="text"
                      value={client.company.address.line1}
                      onChange={(e) => updateCompanyInfo({ address: { ...client.company.address, line1: e.target.value } })}
                      placeholder="1234 Main Street"
                      style={{ ...S.input, marginBottom: 8 }}
                    />
                    <input
                      type="text"
                      value={client.company.address.line2}
                      onChange={(e) => updateCompanyInfo({ address: { ...client.company.address, line2: e.target.value } })}
                      placeholder="Suite 100"
                      style={{ ...S.input, marginBottom: 8 }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
                      <input
                        type="text"
                        value={client.company.address.city}
                        onChange={(e) => updateCompanyInfo({ address: { ...client.company.address, city: e.target.value } })}
                        placeholder="City"
                        style={S.input}
                      />
                      <input
                        type="text"
                        value={client.company.address.state}
                        onChange={(e) => updateCompanyInfo({ address: { ...client.company.address, state: e.target.value } })}
                        placeholder="State"
                        style={S.input}
                      />
                      <input
                        type="text"
                        value={client.company.address.zip}
                        onChange={(e) => updateCompanyInfo({ address: { ...client.company.address, zip: e.target.value } })}
                        placeholder="ZIP"
                        style={S.input}
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: C.text }}>
                    {client.company.address.line1 ? (
                      <>
                        {client.company.address.line1}<br />
                        {client.company.address.line2 && <>{client.company.address.line2}<br /></>}
                        {client.company.address.city}, {client.company.address.state} {client.company.address.zip}
                      </>
                    ) : (
                      <span style={{ color: C.textLight }}>Not set</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
          
          {/* Contacts Section */}
          <section style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                CONTACTS
              </h4>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddContact(true);
                  setEditingContact(null);
                }}
                style={{
                  padding: '4px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.green,
                  background: 'transparent',
                  border: `1px solid ${C.green}`,
                  borderRadius: 4,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                  touchAction: 'manipulation',
                }}
                data-testid="open-add-contact-btn"
              >
                <Icon name="add-outline" style={{ fontSize: 12 }} /> Add Contact
              </button>
            </div>
            
            {client.contacts.length === 0 && !showAddContact ? (
              <div style={{ 
                padding: 24, 
                textAlign: 'center', 
                background: C.bg, 
                borderRadius: 8,
                border: `1px dashed ${C.border}`,
              }}>
                <Icon name="person-outline" style={{ fontSize: 32, color: C.textLight, marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: C.textMid }}>
                  No contacts yet. Add a contact to get started.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Add/Edit Contact Form */}
                {(showAddContact || editingContact) && (
                  <div style={{
                    padding: 16,
                    background: C.blueLight,
                    borderRadius: 8,
                    border: `2px solid ${C.blue}`,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                      {editingContact ? 'Edit Contact' : 'Add New Contact'}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, color: C.textMid, display: 'block', marginBottom: 4 }}>
                          First Name *
                        </label>
                        <input
                          type="text"
                          placeholder="John"
                          defaultValue={editingContact?.firstName || ''}
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: 12,
                            border: `1px solid ${C.border}`,
                            borderRadius: 4,
                            background: 'white',
                          }}
                          id="contact-firstname"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: C.textMid, display: 'block', marginBottom: 4 }}>
                          Last Name *
                        </label>
                        <input
                          type="text"
                          placeholder="Doe"
                          defaultValue={editingContact?.lastName || ''}
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: 12,
                            border: `1px solid ${C.border}`,
                            borderRadius: 4,
                            background: 'white',
                          }}
                          id="contact-lastname"
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: C.textMid, display: 'block', marginBottom: 4 }}>
                        Role/Title
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., HR Manager, CEO"
                        defaultValue={editingContact?.role || ''}
                        style={{
                          width: '100%',
                          padding: '8px',
                          fontSize: 12,
                          border: `1px solid ${C.border}`,
                          borderRadius: 4,
                          background: 'white',
                        }}
                        id="contact-role"
                      />
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, color: C.textMid, display: 'block', marginBottom: 4 }}>
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="john@company.com"
                          defaultValue={editingContact?.email || ''}
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: 12,
                            border: `1px solid ${C.border}`,
                            borderRadius: 4,
                            background: 'white',
                          }}
                          id="contact-email"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: C.textMid, display: 'block', marginBottom: 4 }}>
                          Phone
                        </label>
                        <input
                          type="tel"
                          placeholder="(555) 123-4567"
                          defaultValue={formatPhone(editingContact?.phone || '')}
                          maxLength={14}
                          onInput={(e) => {
                            e.target.value = formatPhone(e.target.value);
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: 12,
                            border: `1px solid ${C.border}`,
                            borderRadius: 4,
                            background: 'white',
                          }}
                          id="contact-phone"
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMid }}>
                        <input
                          type="checkbox"
                          defaultChecked={editingContact?.isPrimary || false}
                          id="contact-isprimary"
                        />
                        Set as primary contact
                      </label>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAddContact(false);
                          setEditingContact(null);
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.textMid,
                          background: C.bg,
                          border: `1px solid ${C.border}`,
                          borderRadius: 4,
                          cursor: 'pointer',
                          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                          touchAction: 'manipulation',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          const firstName = document.getElementById('contact-firstname').value.trim();
                          const lastName = document.getElementById('contact-lastname').value.trim();
                          const role = document.getElementById('contact-role').value.trim();
                          const email = document.getElementById('contact-email').value.trim();
                          const phone = document.getElementById('contact-phone').value.trim();
                          const isPrimary = document.getElementById('contact-isprimary').checked;
                          
                          if (!firstName || !lastName) {
                            alert('Please enter first and last name');
                            return;
                          }
                          
                          const contactData = {
                            id: editingContact?.id,
                            firstName,
                            lastName,
                            role,
                            email,
                            phone,
                            isPrimary,
                          };
                          
                          await saveContact(clientId, contactData);
                          setShowAddContact(false);
                          setEditingContact(null);
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'white',
                          background: C.green,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                          touchAction: 'manipulation',
                        }}
                        data-testid="save-contact-btn"
                      >
                        {editingContact ? 'Save Changes' : 'Add Contact'}
                      </button>
                    </div>
                  </div>
                )}
                
                {client.contacts.map(contact => (
                  <div
                    key={contact.id}
                    style={{
                      padding: 16,
                      background: C.bg,
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon name="person-circle-outline" style={{ fontSize: 20, color: C.textMid }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                            {contact.firstName} {contact.lastName}
                            {contact.isPrimary && (
                              <span style={{ 
                                marginLeft: 8,
                                fontSize: 9, 
                                fontWeight: 700, 
                                color: C.blue,
                                background: C.blueLight,
                                padding: '2px 6px',
                                borderRadius: 3,
                              }}>
                                PRIMARY
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
                            {contact.role}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditingContact(contact);
                            setShowAddContact(false);
                          }}
                          style={{
                            padding: 4,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: C.textMid,
                            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                            touchAction: 'manipulation',
                          }}
                        >
                          <Icon name="pencil-outline" style={{ fontSize: 14 }} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteContact(clientId, contact.id);
                          }}
                          style={{
                            padding: 4,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: C.red,
                            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                            touchAction: 'manipulation',
                          }}
                        >
                          <Icon name="trash-outline" style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    </div>
                    
                    {(contact.email || contact.phone || contact.mobile) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                        {contact.email && (
                          <div style={{ fontSize: 11, color: C.textMid }}>
                            <Icon name="mail-outline" style={{ fontSize: 12, marginRight: 6 }} />
                            {contact.email}
                          </div>
                        )}
                        {contact.phone && (
                          <div style={{ fontSize: 11, color: C.textMid }}>
                            <Icon name="call-outline" style={{ fontSize: 12, marginRight: 6 }} />
                            {formatPhone(contact.phone)}
                          </div>
                        )}
                        {contact.mobile && (
                          <div style={{ fontSize: 11, color: C.textMid }}>
                            <Icon name="phone-portrait-outline" style={{ fontSize: 12, marginRight: 6 }} />
                            {formatPhone(contact.mobile)}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {contact.notes && (
                      <div style={{ 
                        marginTop: 8, 
                        paddingTop: 8, 
                        borderTop: `1px solid ${C.border}`,
                        fontSize: 11,
                        color: C.textMid,
                        fontStyle: 'italic',
                      }}>
                        {contact.notes}
                      </div>
                    )}
                    
                    {/* Portal Access Section */}
                    <div style={{ 
                      marginTop: 12, 
                      paddingTop: 12, 
                      borderTop: `1px solid ${C.border}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: contact.portalAccess ? 8 : 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.textMid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Portal Access
                        </div>
                        {contact.portalAccess ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              background: C.green 
                            }} />
                            <span style={{ fontSize: 10, fontWeight: 700, color: C.green }}>
                              {contact.portalStatus === 'invited' ? 'Invited' : 'Active'}
                            </span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              background: C.textLight 
                            }} />
                            <span style={{ fontSize: 10, fontWeight: 600, color: C.textLight }}>
                              Disabled
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {contact.portalAccess ? (
                        <div>
                          {contact.lastLoginAt && (
                            <div style={{ fontSize: 11, color: C.textMid, marginBottom: 8 }}>
                              Last login: {new Date(contact.lastLoginAt).toLocaleDateString()} at {new Date(contact.lastLoginAt).toLocaleTimeString()}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              onClick={async () => {
                                if (window.confirm(`Disable portal access for ${contact.firstName} ${contact.lastName}? They will no longer be able to log in.`)) {
                                  await disablePortalAccess(clientId, contact.id, currentUser?.id);
                                }
                              }}
                              style={{
                                padding: '4px 10px',
                                fontSize: 10,
                                fontWeight: 600,
                                color: C.orange,
                                background: 'transparent',
                                border: `1px solid ${C.orange}`,
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            >
                              Disable Access
                            </button>
                            <button
                              onClick={async () => {
                                const result = await resetContactPassword(clientId, contact.id, currentUser?.id);
                                if (result.success) {
                                  alert(`New temporary password: ${result.tempPassword}\n\nShare this securely with ${contact.firstName}.`);
                                } else {
                                  alert(`Failed to reset password: ${result.error || 'Unknown error'}`);
                                }
                              }}
                              style={{
                                padding: '4px 10px',
                                fontSize: 10,
                                fontWeight: 600,
                                color: C.accent,
                                background: 'transparent',
                                border: `1px solid ${C.accent}`,
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            >
                              Reset Password
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowEnablePortalModal(contact.id)}
                          disabled={!contact.email}
                          style={{
                            padding: '6px 12px',
                            fontSize: 11,
                            fontWeight: 600,
                            color: !contact.email ? C.textLight : C.accent,
                            background: !contact.email ? C.bg : 'transparent',
                            border: `1px solid ${!contact.email ? C.border : C.accent}`,
                            borderRadius: 6,
                            cursor: !contact.email ? 'not-allowed' : 'pointer',
                            width: '100%',
                          }}
                          title={!contact.email ? 'Email required to enable portal access' : ''}
                        >
                          Enable Access
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          
          {/* Enable Portal Access Modal */}
          {showEnablePortalModal && (
            <EnablePortalAccessModal
              contact={client.contacts.find(c => c.id === showEnablePortalModal)}
              clientName={client.company.legalName || client.company.dba}
              onConfirm={async () => {
                setPortalError(null);
                const result = await enablePortalAccess(clientId, showEnablePortalModal, currentUser?.id);
                if (!result.success) {
                  setPortalError(result.error);
                  setTimeout(() => setPortalError(null), 5000);
                  setShowEnablePortalModal(null);
                }
                return result;
              }}
              onCancel={() => setShowEnablePortalModal(null)}
            />
          )}
          
          {/* Portal Error Toast */}
          {portalError && (
            <div style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              background: C.red,
              color: 'white',
              padding: '12px 16px',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: 13,
              fontWeight: 600,
              zIndex: 10001,
              maxWidth: 400,
            }}>
              {portalError}
            </div>
          )}
          
          {/* Notes Section - Always Visible */}
          <section style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon name="chatbox-outline" style={{ fontSize: 18, color: C.blue }} />
                <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                  NOTES
                </h4>
                <span style={{ 
                  fontSize: 11, 
                  color: C.textMid, 
                  background: C.bg, 
                  padding: '2px 8px', 
                  borderRadius: 12,
                  fontWeight: 600,
                }}>
                  {client.activities.filter(a => a.type === 'note').length}
                </span>
              </div>
            </div>
            
            {/* Add Note Form */}
            <div style={{ marginBottom: 12 }}>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note about a call, meeting, or email..."
                style={{
                  ...S.input,
                  minHeight: 60,
                  resize: 'vertical',
                  fontSize: 12,
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddNote();
                }}
                disabled={!newNote.trim()}
                style={{
                  marginTop: 8,
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#fff',
                  background: newNote.trim() ? C.blue : C.border,
                  border: 'none',
                  borderRadius: 4,
                  cursor: newNote.trim() ? 'pointer' : 'not-allowed',
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                  touchAction: 'manipulation',
                }}
              >
                <Icon name="add-outline" style={{ fontSize: 12 }} /> Add Note
              </button>
            </div>
            
            {/* Notes List */}
            {(() => {
              const notes = client.activities.filter(a => a.type === 'note');
              if (notes.length === 0) {
                return (
                  <div style={{ 
                    padding: 20, 
                    textAlign: 'center', 
                    background: C.bg, 
                    borderRadius: 8,
                    border: `1px dashed ${C.border}`,
                  }}>
                    <div style={{ fontSize: 12, color: C.textMid }}>
                      No notes yet. Add your first note above.
                    </div>
                  </div>
                );
              }
              
              return (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 8,
                }}>
                  {notes.slice(0, 3).map(note => (
                    <div
                      key={note.id}
                      style={{
                        padding: 12,
                        background: C.blueLight,
                        borderRadius: 6,
                        borderLeft: `3px solid ${C.blue}`,
                      }}
                    >
                      <div style={{ fontSize: 10, color: C.textLight, marginBottom: 4 }}>
                        {new Date(note.date).toLocaleString()}
                      </div>
                      <div style={{ fontSize: 12, color: C.text }}>
                        {note.description}
                      </div>
                    </div>
                  ))}
                  {notes.length > 3 && (
                    <div style={{ 
                      fontSize: 11, 
                      color: C.textMid, 
                      textAlign: 'center',
                      padding: '8px 0',
                    }}>
                      + {notes.length - 3} more notes
                    </div>
                  )}
                </div>
              );
            })()}
          </section>


          {/* Employee Enrollment Tracking */}
          {(() => {
            const employees = client.employees || [];
            const employeesWithEmail = employees.filter(emp => emp.email);
            
            if (employeesWithEmail.length === 0) return null;
            
            const statusCounts = {
              not_invited: 0,
              invited: 0,
              enrolled: 0,
              declined: 0
            };
            
            employees.forEach(emp => {
              const status = emp.enrollmentStatus || 'not_invited';
              if (statusCounts.hasOwnProperty(status)) {
                statusCounts[status]++;
              }
            });
            
            const totalInvited = statusCounts.invited + statusCounts.enrolled + statusCounts.declined;
            const completionRate = totalInvited > 0 ? Math.round(((statusCounts.enrolled + statusCounts.declined) / totalInvited) * 100) : 0;
            
            return (
              <section style={{ marginBottom: 32 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: C.text }}>
                  EMPLOYEE ENROLLMENT
                </h4>
                
                <div style={{
                  background: '#fff',
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: 16
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, color: C.textMid, marginBottom: 4 }}>Total</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{employeesWithEmail.length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.textMid, marginBottom: 4 }}>Invited</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'rgb(2, 132, 199)' }}>{statusCounts.invited}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.textMid, marginBottom: 4 }}>Enrolled</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>{statusCounts.enrolled}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.textMid, marginBottom: 4 }}>Declined</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: C.red }}>{statusCounts.declined}</div>
                    </div>
                  </div>
                  
                  {totalInvited > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: C.textMid }}>Completion Rate</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{completionRate}%</span>
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: 6, 
                        background: C.bg, 
                        borderRadius: 3,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${completionRate}%`,
                          height: '100%',
                          background: completionRate === 100 ? C.green : C.accent,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              </section>
            );
          })()}

          {/* System Activity Log - Collapsible */}
          <section style={{ marginBottom: 32 }}>
            {(() => {
              const systemActivities = client.activities.filter(a => a.type !== 'note');
              const latestActivity = systemActivities[0];
              
              return (
                <>
                  <div 
                    onClick={() => setShowActivityLog(!showActivityLog)}
                    style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 8,
                      padding: '12px 16px',
                      background: C.card,
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                    onMouseLeave={(e) => e.currentTarget.style.background = C.card}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon name="time-outline" style={{ fontSize: 18, color: C.textMid }} />
                        <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                          ACTIVITY LOG
                        </h4>
                        <span style={{ 
                          fontSize: 11, 
                          color: C.textMid, 
                          background: C.bg, 
                          padding: '2px 8px', 
                          borderRadius: 12,
                          fontWeight: 600,
                        }}>
                          {systemActivities.length}
                        </span>
                      </div>
                      <Icon 
                        name={showActivityLog ? "chevron-up" : "chevron-down"} 
                        style={{ fontSize: 20, color: C.textMid, transition: 'transform 0.2s' }} 
                      />
                    </div>
                    
                    {/* Latest Activity Preview (when collapsed) */}
                    {!showActivityLog && latestActivity && (
                      <div style={{ 
                        paddingTop: 8,
                        borderTop: `1px solid ${C.border}`,
                        fontSize: 11,
                        color: C.textMid,
                      }}>
                        <div style={{ fontSize: 10, marginBottom: 4, color: C.textLight }}>
                          Latest: {new Date(latestActivity.date).toLocaleString()}
                        </div>
                        <div style={{ color: C.text }}>
                          {latestActivity.description}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {showActivityLog && (
                    <div style={{ marginTop: 16 }}>
                      {systemActivities.length === 0 ? (
                        <div style={{ 
                          padding: 20, 
                          textAlign: 'center', 
                          background: C.bg, 
                          borderRadius: 8,
                          border: `1px dashed ${C.border}`,
                        }}>
                          <div style={{ fontSize: 12, color: C.textMid }}>
                            No system activity yet.
                          </div>
                        </div>
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 8,
                          maxHeight: 400,
                          overflowY: 'auto',
                          padding: '8px 4px',
                        }}>
                          {systemActivities.map(activity => (
                            <div
                              key={activity.id}
                              style={{
                                padding: 12,
                                background: C.bg,
                                borderRadius: 6,
                                borderLeft: `3px solid ${C.border}`,
                              }}
                            >
                              <div style={{ fontSize: 10, color: C.textLight, marginBottom: 4 }}>
                                {new Date(activity.date).toLocaleString()}
                              </div>
                              <div style={{ fontSize: 12, color: C.text }}>
                                {activity.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </section>
          
          {/* IBOS Configuration Section */}
          <IBOSClientSection client={client} saveClient={saveClient} ibosLayers={config?.config?.ibosLayers || []} />

          {/* Saved Options panel — visible only when options have been generated */}
          <SavedOptionsAdminPanel client={client} onSave={saveClient} />

          {/* Client Messages Section */}
          <section data-testid="drawer-messages-section" style={{ marginBottom: 32 }}>
            <MessagesSection
              clientId={client.id || client._id}
              currentUserId={currentUser?.id || currentUser?._id}
              variant="internal"
              compact
            />
          </section>

          {/* Analysis Configuration Section */}
          <section style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                ANALYSIS CONFIGURATION
              </h4>
              <div style={{ display: 'flex', gap: 8 }}>
                {client.analysisConfig?.isLocked ? (
                  <button
                    onClick={() => setShowUnlockConfirm(true)}
                    style={{
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.orange,
                      background: 'transparent',
                      border: `1px solid ${C.orange}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icon name="lock-open-outline" style={{ fontSize: 14 }} />
                    Unlock
                  </button>
                ) : (
                  <button
                    onClick={() => setEditingAnalysisConfig(true)}
                    style={{
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.accent,
                      background: 'transparent',
                      border: `1px solid ${C.accent}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icon name="create-outline" style={{ fontSize: 14 }} />
                    Edit
                  </button>
                )}
                {client.analysisConfig?.isLocked && (
                  <div style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.red,
                    background: C.redLight,
                    border: `1px solid ${C.red}`,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <Icon name="lock-closed" style={{ fontSize: 14 }} />
                    Locked
                  </div>
                )}
              </div>
            </div>

            {/* Display Mode */}
            {!editingAnalysisConfig ? (
              <div>
                {/* Status Bar */}
                <div style={{
                  padding: 12,
                  background: client.analysisConfig?.isLocked ? C.redLight : C.greenLight,
                  borderRadius: 6,
                  marginBottom: 16,
                  fontSize: 11,
                  color: client.analysisConfig?.isLocked ? C.red : C.green,
                  fontWeight: 600,
                }}>
                  {client.analysisConfig?.isLocked ? (
                    <>
                      🔒 Locked — Proposal sent {client.analysisConfig.lockedAt ? new Date(client.analysisConfig.lockedAt).toLocaleDateString() : ''}
                      {client.analysisConfig.lockedBy && getUserById(client.analysisConfig.lockedBy) && (
                        <> by {getUserById(client.analysisConfig.lockedBy).firstName} {getUserById(client.analysisConfig.lockedBy).lastName}</>
                      )}
                    </>
                  ) : (
                    <>
                      ● Active (editable)
                      {client.analysisConfig?.lastModifiedAt && (
                        <> · Last modified {new Date(client.analysisConfig.lastModifiedAt).toLocaleDateString()}</>
                      )}
                      {client.analysisConfig?.lastModifiedBy && getUserById(client.analysisConfig.lastModifiedBy) && (
                        <> by {getUserById(client.analysisConfig.lastModifiedBy).firstName} {getUserById(client.analysisConfig.lastModifiedBy).lastName}</>
                      )}
                    </>
                  )}
                </div>

                {/* Settings Display */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Employer Overrides */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, marginBottom: 10, textTransform: 'uppercase' }}>
                      Employer Overrides
                    </div>
                    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.8 }}>
                      <div><strong>Waiting Period:</strong> {client.analysisConfig?.overrides?.waitingPeriod || 0} days</div>
                      <div><strong>Min Weekly Hours:</strong> {client.analysisConfig?.overrides?.minWeeklyHours || 0}</div>
                      <div><strong>Eligible Classes:</strong> {
                        (() => {
                          const classes = client.analysisConfig?.overrides?.eligibleClasses;
                          if (!classes || typeof classes !== 'object') return 'Default';
                          const classNames = [];
                          if (classes.fulltime_w2) classNames.push('Full-Time W-2');
                          if (classes.parttime_w2) classNames.push('Part-Time W-2');
                          if (classes.seasonal_temp) classNames.push('Seasonal/Temp');
                          if (classes.union_cba) classNames.push('Union/CBA');
                          return classNames.length > 0 ? classNames.join(', ') : 'None';
                        })()
                      }</div>
                      <div><strong>Local Min Wage:</strong> ${(client.analysisConfig?.overrides?.localMinWage || 7.25).toFixed(2)}/hr</div>
                      <div><strong>Available Plans:</strong> {(client.analysisConfig?.overrides?.availablePlanTiers || []).length} of 6</div>
                    </div>
                  </div>

                  {/* Analysis Controls */}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, marginBottom: 10, textTransform: 'uppercase' }}>
                      Analysis Controls
                    </div>
                    <div style={{ fontSize: 12, color: C.text, lineHeight: 1.8 }}>
                      <div><strong>Pay Frequency:</strong> {
                        client.analysisConfig?.controls?.payFrequency === 12 ? 'Monthly (12)' :
                        client.analysisConfig?.controls?.payFrequency === 24 ? 'Semi-monthly (24)' :
                        client.analysisConfig?.controls?.payFrequency === 26 ? 'Biweekly (26)' :
                        client.analysisConfig?.controls?.payFrequency === 52 ? 'Weekly (52)' : 'Biweekly (26)'
                      }</div>
                      <div><strong>Optimization:</strong> {
                        client.analysisConfig?.controls?.optimizationStrategy === 'employee_benefit' ? 'Employee Benefit' :
                        client.analysisConfig?.controls?.optimizationStrategy === 'employer_savings' ? 'Employer Savings' :
                        client.analysisConfig?.controls?.optimizationStrategy === 'balanced' ? 'Balanced' : 'Employee Benefit'
                      }</div>
                      <div><strong>Min Employee Net:</strong> ${(client.analysisConfig?.controls?.minEmployeeNet || 0).toFixed(2)}</div>
                      <div><strong>Include FUTA:</strong> {client.analysisConfig?.controls?.includeFuta ? 'Yes' : 'No'}</div>
                    </div>
                  </div>
                </div>

                {/* Differs from Defaults */}
                {(() => {
                  const defaults = config;
                  const diffs = [];
                  
                  if ((client.analysisConfig?.overrides?.waitingPeriod || 0) !== (defaults?.employerDefaults?.waitingPeriod || 60)) {
                    diffs.push(`Waiting period: ${client.analysisConfig?.overrides?.waitingPeriod || 0} days (default: ${defaults?.employerDefaults?.waitingPeriod || 60})`);
                  }
                  if ((client.analysisConfig?.overrides?.minWeeklyHours || 0) !== (defaults?.employerDefaults?.minWeeklyHours || 30)) {
                    diffs.push(`Min weekly hours: ${client.analysisConfig?.overrides?.minWeeklyHours || 0} (default: ${defaults?.employerDefaults?.minWeeklyHours || 30})`);
                  }
                  if ((client.analysisConfig?.overrides?.availablePlanTiers || []).length < 6) {
                    diffs.push(`Available plans: ${(client.analysisConfig?.overrides?.availablePlanTiers || []).length} of 6 (default: all)`);
                  }
                  if ((client.analysisConfig?.overrides?.localMinWage || 7.25) !== 7.25) {
                    diffs.push(`Local min wage: $${(client.analysisConfig?.overrides?.localMinWage || 7.25).toFixed(2)} (default: $7.25)`);
                  }

                  if (diffs.length > 0) {
                    return (
                      <div style={{
                        marginTop: 16,
                        padding: 12,
                        background: C.blueLight,
                        border: `1px solid ${C.blue}`,
                        borderRadius: 6,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 8 }}>
                          Differs from system defaults:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11, color: C.textMid, lineHeight: 1.6 }}>
                          {diffs.map((diff, i) => (
                            <li key={i}>{diff}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ) : (
              /* Edit Mode */
              <div style={{
                padding: 20,
                background: C.bg,
                borderRadius: 8,
                border: `1px solid ${C.border}`,
              }}>
                <AnalysisConfigEditor
                  config={client.analysisConfig}
                  systemDefaults={config}
                  onSave={(newConfig) => {
                    updateAnalysisConfig(clientId, newConfig, currentUser.id);
                    setEditingAnalysisConfig(false);
                  }}
                  onCancel={() => setEditingAnalysisConfig(false)}
                />
              </div>
            )}
          </section>
          
          {/* Analysis Summary Section */}
          <section style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                ANALYSIS SUMMARY
              </h4>
            </div>
            
            {!client.summary || !client.employees.length ? (
              showSmartUpload ? (
                <div data-testid={`smart-upload-drawer-${clientId}`}>
                  <SmartCensusUpload
                    client={client}
                    onUploaded={({ employees: parsed, meta }) => {
                      // Persist locally too — SmartCensusUpload already POSTed
                      // to /api/clients/{id}/census/upload. We mirror the
                      // saveClient path so the drawer's local view refreshes
                      // immediately without requiring a page reload.
                      // Also recompute the FULL summary (eligible / FICA /
                      // benefit / hcRevenue / sub-buckets) so the Clients
                      // dropdown, Clients table and Update-Client modal stay
                      // accurate without a manual page refresh.
                      const newSummary = buildClientSummaryFromEmployees(parsed, config);
                      saveClient({
                        ...client,
                        employees: parsed,
                        originalCsvText: client?.originalCsvText || '',
                        summary: newSummary,
                      });
                      setShowSmartUpload(false);
                    }}
                    onCancel={() => setShowSmartUpload(false)}
                  />
                </div>
              ) : (
                <div style={{
                  padding: 24,
                  textAlign: 'center',
                  background: C.bg,
                  borderRadius: 8,
                  border: `1px dashed ${C.border}`,
                }}>
                  <Icon name="bar-chart-outline" style={{ fontSize: 32, color: C.textLight, marginBottom: 8 }} />
                  <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
                    No census data uploaded yet.
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSmartUpload(true);
                    }}
                    data-testid={`drawer-open-smart-upload-${clientId}`}
                    style={{
                      padding: '8px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#fff',
                      background: C.blue,
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                      touchAction: 'manipulation',
                    }}
                  >
                    <Icon name="cloud-upload-outline" style={{ fontSize: 14 }} /> Smart Upload Census
                  </button>
                  <div style={{ marginTop: 8, fontSize: 11, color: C.textLight }}>
                    Supports CSV, XLSX, XLS · auto-detects ADP / HealthCues / Paycom / Paycor / Gusto / generic formats
                  </div>
                </div>
              )
            ) : (
              <div style={{ background: C.bg, padding: 16, borderRadius: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.textLight, marginBottom: 2 }}>Total Employees</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{client.summary.totalEmployees || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textLight, marginBottom: 2 }}>Eligible</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.green }}>
                      {client.summary.eligible || 0} ({Math.round(((client.summary.eligible || 0) / (client.summary.totalEmployees || 1)) * 100)}%)
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textLight, marginBottom: 2 }}>Annual FICA Savings</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>
                      {fmt(client.summary.annualEmployerFica || 0, 0)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textLight, marginBottom: 2 }}>Annual HC Revenue</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.blue }}>
                      {fmt(client.summary.annualHcRevenue || 0, 0)}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenAnalysis();
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.blue,
                    background: C.blueLight,
                    border: `1px solid ${C.blue}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                    touchAction: 'manipulation',
                  }}
                >
                  <Icon name="bar-chart-outline" style={{ fontSize: 14 }} /> View Full Analysis
                </button>
              </div>
            )}
          </section>
          
          {/* Contracts Section */}
          <section style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: C.text }}>
                CONTRACTS
              </h4>
              {selectedTemplates.length > 0 && (
                <button
                  onClick={() => handleAssignContracts('manual')}
                  disabled={assigningContracts}
                  style={{
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'white',
                    background: assigningContracts ? C.textLight : C.accent,
                    border: 'none',
                    borderRadius: 6,
                    cursor: assigningContracts ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon name="checkmark-circle-outline" style={{ fontSize: 14 }} />
                  {assigningContracts ? 'Assigning...' : `Assign Selected (${selectedTemplates.length})`}
                </button>
              )}
            </div>
            
            {contractTemplates.length === 0 ? (
              <div style={{ 
                padding: 24, 
                textAlign: 'center', 
                background: C.bg, 
                borderRadius: 8,
                border: `1px dashed ${C.border}`,
              }}>
                <Icon name="document-outline" style={{ fontSize: 32, color: C.textLight, marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: C.textMid }}>
                  No contract templates available
                </div>
              </div>
            ) : (
              <>
                <div style={{ 
                  background: C.card, 
                  border: `1px solid ${C.border}`, 
                  borderRadius: 8,
                  overflow: 'hidden',
                }}>
                  {contractTemplates.map((template, index) => {
                    const assignedContract = client.contracts?.find(c => c.templateId === template.id);
                    const isAssigned = !!assignedContract;
                    
                    return (
                      <div 
                        key={template.id}
                        style={{
                          padding: '12px 16px',
                          borderBottom: index < contractTemplates.length - 1 ? `1px solid ${C.borderLight}` : 'none',
                          background: isAssigned ? C.accentLight : 'transparent',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          {!isAssigned && (
                            <input
                              type="checkbox"
                              checked={selectedTemplates.includes(template.id)}
                              onChange={() => handleToggleTemplate(template.id)}
                              style={{
                                width: 16,
                                height: 16,
                                cursor: 'pointer',
                              }}
                            />
                          )}
                          
                          <Icon 
                            name={isAssigned ? "checkmark-circle" : "document-text-outline"} 
                            style={{ 
                              fontSize: 20, 
                              color: isAssigned ? C.green : C.textMid,
                              marginLeft: isAssigned ? 4 : 0,
                            }} 
                          />
                          
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ 
                                fontSize: 13, 
                                fontWeight: 600, 
                                color: C.text 
                              }}>
                                {template.name}
                              </span>
                              {template.isRequired && (
                                <span style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: C.red,
                                  background: C.redLight,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                }}>
                                  REQUIRED
                                </span>
                              )}
                              {assignedContract && (
                                <span style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: assignedContract.assignmentType === 'auto' ? C.purple : C.blue,
                                  background: assignedContract.assignmentType === 'auto' ? C.purpleLight : C.blueLight,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                }}>
                                  {assignedContract.assignmentType === 'auto' ? 'AUTO' : 'MANUAL'}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>
                              {template.shortName} • {template.description}
                            </div>
                            {assignedContract && (
                              <div style={{ fontSize: 11, color: C.textMid, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Icon name="time-outline" style={{ fontSize: 12 }} />
                                Status: <span style={{ 
                                  fontWeight: 600,
                                  color: assignedContract.status === 'signed' ? C.green : C.orange 
                                }}>
                                  {assignedContract.status.replace(/_/g, ' ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {contractTemplates.some(t => t.isRequired) && !client.contracts?.length && (
                  <button
                    onClick={() => handleAssignContracts('auto')}
                    disabled={assigningContracts}
                    style={{
                      marginTop: 12,
                      padding: '8px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      borderRadius: 6,
                      cursor: assigningContracts ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Icon name="flash-outline" style={{ fontSize: 14 }} />
                    Auto-assign Required Contracts
                  </button>
                )}
              </>
            )}
          </section>
          
          {/* Employer Portal Part 2: Banking, Census & Payroll Section */}
          <section style={{ marginBottom: 32 }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 16, color: C.text }}>
              ONBOARDING PROGRESS (PART 2)
            </h4>
            
            {/* Banking */}
            <div style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <Icon 
                  name={client.company?.ach ? "checkmark-circle" : "business-outline"} 
                  style={{ 
                    fontSize: 24, 
                    color: client.company?.ach ? C.green : C.textMid 
                  }} 
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    Banking / ACH Setup
                  </div>
                  <div style={{ fontSize: 11, color: C.textMid }}>
                    {client.company?.ach ? 'Connected' : 'Not set up yet'}
                  </div>
                </div>
                {client.company?.ach && (
                  <div style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: client.company.ach.verificationStatus === 'verified' ? C.green : C.orange,
                    background: client.company.ach.verificationStatus === 'verified' ? C.greenLight : C.orangeLight,
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}>
                    {client.company.ach.verificationStatus === 'verified' ? 'VERIFIED' : 'PENDING'}
                  </div>
                )}
              </div>
              
              {client.company?.ach && (
                <div style={{
                  background: C.bg,
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 12,
                }}>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Bank:</span>
                      <span style={{ fontWeight: 600, color: C.text }}>{client.company.ach.bankName}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Account:</span>
                      <span style={{ fontWeight: 600, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {client.company.ach.accountMasked}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Routing:</span>
                      <span style={{ fontWeight: 600, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {client.company.ach.routingMasked}
                      </span>
                    </div>
                    {client.company.ach.verifiedAt && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                        <span style={{ color: C.textMid }}>Verified via:</span>
                        <span style={{ fontWeight: 600, color: C.text }}>
                          {client.company.ach.verifiedVia === 'plaid' ? 'Plaid' : 'Micro-deposits'} · {new Date(client.company.ach.verifiedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Census */}
            <div style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <Icon 
                  name={client.employees?.length > 0 ? "checkmark-circle" : "people-outline"} 
                  style={{ 
                    fontSize: 24, 
                    color: client.employees?.length > 0 ? C.green : C.textMid 
                  }} 
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    Employee Census
                  </div>
                  <div style={{ fontSize: 11, color: C.textMid }}>
                    {client.employees?.length > 0 ? `${client.employees.length} employees uploaded` : 'Not uploaded yet'}
                  </div>
                </div>
                {client.employees?.length > 0 && (
                  <div style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.green,
                    background: C.greenLight,
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}>
                    COMPLETE
                  </div>
                )}
              </div>
              
              {client.employees?.length > 0 && (
                <div style={{
                  background: C.bg,
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 12,
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ color: C.textMid, marginBottom: 4 }}>Total Employees:</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{client.employees.length}</div>
                    </div>
                    <div>
                      <div style={{ color: C.textMid, marginBottom: 4 }}>Avg Salary:</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                        ${Math.round(client.employees.reduce((sum, e) => sum + parseFloat(e.annualSalary || 0), 0) / client.employees.length).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Payroll */}
            <div style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <Icon 
                  name={client.payroll ? "checkmark-circle" : "calendar-outline"} 
                  style={{ 
                    fontSize: 24, 
                    color: client.payroll ? C.green : C.textMid 
                  }} 
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    Payroll Integration
                  </div>
                  <div style={{ fontSize: 11, color: C.textMid }}>
                    {client.payroll ? `${client.payroll.providerName} connected` : 'Not configured yet'}
                  </div>
                </div>
                {client.payroll && (
                  <div style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: C.green,
                    background: C.greenLight,
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}>
                    ACTIVE
                  </div>
                )}
              </div>
              
              {client.payroll && (
                <div style={{
                  background: C.bg,
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 12,
                }}>
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Provider:</span>
                      <span style={{ fontWeight: 600, color: C.text }}>{client.payroll.providerName}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Schedule:</span>
                      <span style={{ fontWeight: 600, color: C.text }}>
                        {client.payroll.paySchedule === 'biweekly' ? 'Biweekly' : 
                         client.payroll.paySchedule === 'weekly' ? 'Weekly' :
                         client.payroll.paySchedule === 'semimonthly' ? 'Semi-monthly' : 'Monthly'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Next Pay Date:</span>
                      <span style={{ fontWeight: 600, color: C.text }}>
                        {new Date(client.payroll.nextPayDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>Deduction Code:</span>
                      <span style={{ fontWeight: 600, color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {client.payroll.deductionCode}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: C.textMid }}>File Format:</span>
                      <span style={{ fontWeight: 600, color: C.text }}>
                        {client.payroll.fileFormat || `${client.payroll.providerName} CSV`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
          
          {/* Documents Section */}
          <section>
            <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 16, color: C.text }}>
              DOCUMENTS
            </h4>
            
            {client.documents.length === 0 ? (
              <div style={{ 
                padding: 24, 
                textAlign: 'center', 
                background: C.bg, 
                borderRadius: 8,
                border: `1px dashed ${C.border}`,
              }}>
                <Icon name="document-outline" style={{ fontSize: 32, color: C.textLight, marginBottom: 8 }} />
                <div style={{ fontSize: 12, color: C.textMid }}>
                  No documents yet. Generated PDFs and CSVs will appear here.
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {client.documents.map(doc => (
                  <div
                    key={doc.id}
                    style={{
                      padding: 12,
                      background: C.bg,
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon name="document-text-outline" style={{ fontSize: 20, color: C.blue }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{doc.name}</div>
                        <div style={{ fontSize: 10, color: C.textLight }}>
                          {new Date(doc.generatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <button
                      style={{
                        padding: 6,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: C.blue,
                      }}
                    >
                      <Icon name="download-outline" style={{ fontSize: 18 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
      
      {/* Unlock Analysis Config Modal */}
      {showUnlockConfirm && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => {
            setShowUnlockConfirm(false);
            setUnlockReason('');
          }}
        >
          <div 
            style={{
              background: C.card,
              borderRadius: 12,
              padding: '24px 28px',
              width: '90%',
              maxWidth: 440,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Icon name="lock-open-outline" style={{ fontSize: 24, color: C.orange }} />
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>
                Unlock Analysis Settings
              </h3>
            </div>
            
            <p style={{ fontSize: 13, color: C.textMid, margin: '0 0 16px', lineHeight: 1.5 }}>
              This will allow editing of the employer overrides and analysis controls.
              Please provide a reason for unlocking.
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.textMid, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Reason <span style={{ color: C.red }}>*</span>
              </label>
              <textarea
                data-testid="unlock-reason-input"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="e.g., Client requested changes to waiting period"
                autoFocus
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowUnlockConfirm(false);
                  setUnlockReason('');
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textMid,
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                data-testid="unlock-confirm-btn"
                onClick={() => {
                  if (!unlockReason.trim()) {
                    alert('Please provide a reason for unlocking');
                    return;
                  }
                  unlockAnalysisConfig(client.id, currentUser?.id, unlockReason.trim());
                  setShowUnlockConfirm(false);
                  setUnlockReason('');
                }}
                disabled={!unlockReason.trim()}
                style={{
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'white',
                  background: unlockReason.trim() ? C.orange : '#ccc',
                  border: 'none',
                  borderRadius: 6,
                  cursor: unlockReason.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Unlock Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── IBOS Client-Level Configuration Section ──────────────────────
function IBOSClientSection({ client, saveClient, ibosLayers }) {
  const [expanded, setExpanded] = React.useState(false);
  const [showS105Wizard, setShowS105Wizard] = React.useState(false);
  const clientIbosConfig = client.ibosConfig || {};
  const perLayer = clientIbosConfig.layers || {};
  const s105 = clientIbosConfig.section105 || null;

  if (!ibosLayers || ibosLayers.length === 0) return null;

  const toggleLayer = (layerId, currentEnabled) => {
    const nextLayers = {
      ...perLayer,
      [layerId]: {
        ...(perLayer[layerId] || {}),
        enabled: !currentEnabled,
      },
    };
    saveClient({
      ...client,
      ibosConfig: { ...clientIbosConfig, layers: nextLayers },
    });
  };

  const updateInput = (layerId, inputKey, value) => {
    const nextLayers = {
      ...perLayer,
      [layerId]: {
        ...(perLayer[layerId] || {}),
        inputs: {
          ...((perLayer[layerId] && perLayer[layerId].inputs) || {}),
          [inputKey]: value,
        },
      },
    };
    saveClient({
      ...client,
      ibosConfig: { ...clientIbosConfig, layers: nextLayers },
    });
  };

  const totalOverrides = Object.keys(perLayer).length;

  return (
    <section style={{ marginBottom: 32 }} data-testid="client-ibos-section">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          background: expanded ? C.accentBg : C.card,
          cursor: 'pointer',
          marginBottom: expanded ? 12 : 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="layers-outline" style={{ fontSize: 18, color: C.accent }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>IBOS Configuration</div>
            <div style={{ fontSize: 11, color: C.textLight }}>
              {totalOverrides > 0
                ? `${totalOverrides} layer override(s) for this employer`
                : 'Using admin defaults — click to customize'}
            </div>
          </div>
        </div>
        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} style={{ fontSize: 16, color: C.textMid }} />
      </button>

      {expanded && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.card }}>
          {/* Section 105 Plan Advisor card */}
          <div
            data-testid="s105-launcher"
            style={{
              padding: 14,
              marginBottom: 12,
              borderRadius: 8,
              background: C.accentBg,
              border: `1px solid ${C.accentLight}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: C.accent,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon name="medical-outline" style={{ fontSize: 20 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    Section 105 Plan Advisor
                  </div>
                  <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.4 }}>
                    {s105 && s105.answers
                      ? `Recommended: ${s105.recommendedPlanId || 'saved'}${s105.presentedToEmployer ? ' • Sent to Employer' : ''}`
                      : '6 questions → HRA recommendation + eligibility bridge + plan documents'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowS105Wizard(true)}
                data-testid="open-s105-wizard-btn"
                style={{
                  padding: '8px 14px',
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {s105 && s105.answers ? 'View results' : 'Launch wizard'}
                <Icon name="arrow-forward" style={{ fontSize: 12, marginLeft: 6, verticalAlign: 'middle' }} />
              </button>
            </div>

            {/* Show in Employer Portal toggle (default ON) */}
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: `1px solid ${C.accentLight}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                  Show advisor in Employer Portal
                </div>
                <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.4 }}>
                  Lets the employer see and start the wizard from their dashboard.
                </div>
              </div>
              <Section105PortalToggle client={client} saveClient={saveClient} />
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ ...S.th, textAlign: 'left' }}>Layer</th>
                <th style={{ ...S.th, textAlign: 'center' }}>Status</th>
                <th style={{ ...S.th, textAlign: 'center' }}>Enabled for Client</th>
                <th style={{ ...S.th, textAlign: 'left' }}>Primary Input</th>
              </tr>
            </thead>
            <tbody>
              {ibosLayers.map((layer) => {
                const override = perLayer[layer.id] || {};
                const enabled = override.enabled !== undefined ? override.enabled : layer.defaultEnabled;
                const isToggleable = layer.defaultStatus === 'active' || layer.defaultStatus === 'available';
                // Primary input is the first numeric input (e.g. monthlyContribution, avgAnnualRxSpendPerEmployee, etc.)
                const primaryKey = Object.keys(layer.inputs || {}).find((k) => typeof layer.inputs[k] === 'number');
                const adminDefault = primaryKey ? layer.inputs[primaryKey] : null;
                const overrideVal = override.inputs && override.inputs[primaryKey];
                return (
                  <tr key={layer.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600, color: C.text }}>{layer.name}</div>
                      <div style={{ fontSize: 10, color: C.textLight }}>Layer {layer.order}</div>
                    </td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: C.bg, color: C.textMid, fontWeight: 700 }}>
                        {(layer.defaultStatus || 'na').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!enabled}
                        disabled={!isToggleable}
                        onChange={() => toggleLayer(layer.id, enabled)}
                        data-testid={`client-ibos-toggle-${layer.id}`}
                      />
                    </td>
                    <td style={S.td}>
                      {primaryKey && layer.id !== 'section125' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="number"
                            step="any"
                            value={overrideVal !== undefined ? overrideVal : adminDefault}
                            onChange={(e) => updateInput(layer.id, primaryKey, parseFloat(e.target.value) || 0)}
                            style={{ ...S.input, fontSize: 11, padding: '4px 6px', width: 100 }}
                            title={primaryKey}
                          />
                          {overrideVal !== undefined && overrideVal !== adminDefault && (
                            <span style={{ fontSize: 9, color: C.accent, fontWeight: 700 }}>OVERRIDE</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 10, color: C.textLight }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showS105Wizard && (
        <div
          data-testid="s105-wizard-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setShowS105Wizard(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 1100,
              maxHeight: '92vh',
              background: C.bg,
              borderRadius: 12,
              overflow: 'auto',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            }}
          >
            <Section105Wizard
              clientId={client.id}
              mode="internal"
              onClose={() => setShowS105Wizard(false)}
            />
          </div>
        </div>
      )}
    </section>
  );
}


// ─── Section 105 Portal-visibility toggle ──────────────────────────
function Section105PortalToggle({ client, saveClient }) {
  const existingIbos = client.ibosConfig || {};
  const existing105 = existingIbos.section105 || {};
  // Default to ON when undefined — admins can opt out per-client.
  const isOn = existing105.showInPortal !== false;
  const [pending, setPending] = React.useState(false);

  const toggle = async () => {
    if (pending) return;
    setPending(true);
    try {
      await saveClient({
        ...client,
        ibosConfig: {
          ...existingIbos,
          section105: { ...existing105, showInPortal: !isOn },
        },
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      role="switch"
      aria-checked={isOn}
      data-testid="s105-portal-visibility-toggle"
      style={{
        position: 'relative',
        width: 44,
        height: 24,
        borderRadius: 999,
        background: isOn ? C.accent : C.border,
        border: 'none',
        cursor: pending ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        opacity: pending ? 0.6 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: isOn ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 150ms ease',
        }}
      />
    </button>
  );
}
